import React, { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import Dashboard from '../../services/Dashboard'
import Tecnicos from '../../services/Tecnicos'
import {
  baseYear, comparisonVsLabel, dailyRows, formatDecimal, formatNumber, formatPercent, formatShare, futureAtendimentosText,
  kpiComparison, monthlyAccumulated, monthlyRows, monthShortName, monthVariationText, partialBaseText, partialPreviousText, periodLabel, plural,
  resolveProntuariosPeriod, resolveTecnicoId, tecnicoLabel, tecnicoOptions, todayString, unmappedStatusText, weekdayRows,
} from '../../utilities/dashboard'
import { BlockError, KpiSkeleton, WarningStrip } from './BlockState'
import ChartCard, { DataTable } from './charts/ChartCard'
import DailyChart from './charts/DailyChart'
import MonthlyComparisonChart, { MonthlyLegend } from './charts/MonthlyComparisonChart'
import ReasonsChart from './charts/ReasonsChart'
import useChartColors from './charts/useChartColors'
import WeekdayChart from './charts/WeekdayChart'
import DashboardFilters from './DashboardFilters'
import { useDashboardBlock, useIsNarrow } from './hooks'
import { CardTitle } from './InfoButton'
import KpiCard, { Comparison } from './KpiCard'

const blockState = (block) => ({
  skeleton: block.loading && !block.data,
  error: block.error,
  onRetry: block.reload,
  stale: block.loading && Boolean(block.data),
})

// Meses do ano base que o período cobre (até hoje), para o destaque cinza no mês a mês
function selectedMonths(period, year, today) {
  const start = (period.start_date < `${year}-01-01`) ? `${year}-01-01` : period.start_date
  const end = (period.end_date > today) ? today : period.end_date
  if (start > end) return null
  return { from: monthShortName(Number(start.slice(5, 7))), to: monthShortName(Number(end.slice(5, 7))) }
}

// Aba Prontuário: um filtro (doutor e período) para a aba toda. summary e days refazem a cada mudança; months só quando
// muda o ano base ou o doutor. As três correm em paralelo e um erro numa não derruba as outras
const TabProntuarios = ({ params, onParamsChange, onForbidden }) => {
  const { chart: chartColors } = useChartColors()
  const narrow = useIsNarrow()
  const [tecnicos, setTecnicos] = useState([])
  const today = todayString()
  const period = resolveProntuariosPeriod(params, today)
  const tecnico_id = resolveTecnicoId(params.tecnico_id)
  const year = baseYear(period.end_date, today)
  const filters = { start_date: period.start_date, end_date: period.end_date, tecnico_id }
  const periodKey = `${period.start_date}|${period.end_date}|${tecnico_id}`

  const summary = useDashboardBlock(periodKey, () => Dashboard.findProntuariosSummary(filters), onForbidden)
  const days = useDashboardBlock(periodKey, () => Dashboard.findProntuariosDays(filters), onForbidden)
  const months = useDashboardBlock(`${year}|${tecnico_id}`, () => Dashboard.findProntuariosMonths({ year, tecnico_id }), onForbidden)

  // Todos os doutores, com os inativos (o filtro mostra "(INATIVO)")
  useEffect(() => {
    Tecnicos.listForSelect()
      .then(({ data }) => setTecnicos(data.data || []))
      .catch(() => setTecnicos([]))
  }, [])

  const tecnico = tecnicos.find((item) => item.id === tecnico_id)
  const scopeLabel = tecnico_id ? (tecnico ? tecnicoLabel(tecnico) : 'doutor escolhido') : 'todos os doutores'
  const periodText = periodLabel(period, today)

  const changePeriod = ({ start_date, end_date }) => onParamsChange({ start_date, end_date, tecnico_id })
  const changeTecnico = (value) => onParamsChange({ start_date: params.start_date || '', end_date: params.end_date || '', tecnico_id: value })

  // ---------- cards ----------
  const data = summary.data
  const current = data?.current
  const base = data?.compared || current // percentuais e média por dia: só dias completos no período em andamento
  const previous = data?.previous
  const variation = data?.variation_percent || {}
  const status = data?.comparison?.status
  const vs = comparisonVsLabel(data?.comparison)
  // Base zero: o texto depende de o ano anterior ter tido atendimento; MIN_DAYS: a data depende do período da resposta
  const compare = (key, options = {}) => kpiComparison({
    indicator: key, status, variation: variation[key], current: base?.[key], previous: previous?.[key],
    previousAtendimentos: previous?.total_atendimentos, period: { start_date: data?.start_date, end_date: data?.end_date }, vs, ...options,
  })

  const warnings = [
    unmappedStatusText(current?.unmapped_status),
    futureAtendimentosText(current?.future_atendimentos),
  ].filter(Boolean)

  // ---------- gráficos ----------
  const dayRows = dailyRows(days.data, today)
  const monthlyByMonth = days.data?.granularity === 'month'
  const dailyTitle = monthlyByMonth ? `Atendimentos por mês — ${periodText}` : `Atendimentos por dia — ${periodText}`
  const dailyAverage = days.data?.daily_average ?? null
  const monthRows = monthlyRows(months.data)
  const accumulated = months.data ? monthlyAccumulated(months.data) : null
  const monthsYear = months.data?.year ?? year
  const monthsPreviousYear = months.data?.previous_year ?? (year - 1)
  const monthsTitle = `Atendimentos por mês — ${monthsYear} × ${monthsPreviousYear}`
  const weekdays = weekdayRows(days.data?.weekdays)
  const byTecnico = data?.by_tecnico || []
  const sumPacientes = byTecnico.reduce((sum, row) => sum + row.total_pacientes, 0)

  return (
    <div className='dashboard-section'>
      <DashboardFilters
        tecnicoOptions={tecnicoOptions(tecnicos)}
        tecnicoId={tecnico_id}
        onTecnicoChange={changeTecnico}
        period={period}
        today={today}
        firstYear={months.data?.first_year}
        periodText={periodText}
        onPeriodChange={changePeriod}
      />

      {warnings.map((text) => <WarningStrip key={text}>{text}</WarningStrip>)}

      {summary.error ?
        <BlockError message={summary.error} onRetry={summary.reload} />
        :
        <div className={`dashboard-kpis ${(summary.loading && data) ? 'dashboard-stale' : ''}`}>
          {!current ? <KpiSkeleton /> :
            <>
              <KpiCard
                label='Atendimentos'
                info='prontuarios_atendimentos'
                value={formatNumber(current.total_atendimentos)}
                sub={current.total_without_record
                  ? `${formatNumber(current.total_atendimentos - current.total_without_record)} evoluções + ${plural(current.total_without_record, 'sessão sem evolução', 'sessões sem evolução')}`
                  : (current.total_atendimentos ? 'todos com evolução no prontuário' : 'nenhum atendimento')}
                comparison={compare('total_atendimentos')}
              />
              <KpiCard label='Pacientes atendidos' info='prontuarios_pacientes' value={formatNumber(current.total_pacientes)} sub='pacientes diferentes' comparison={compare('total_pacientes')} />
              <KpiCard
                label='Média por dia'
                info='prontuarios_daily_average'
                value={formatDecimal(base.daily_average)}
                sub={`${data.compared ? 'até ontem, ' : ''}em ${plural(base.days_with_atendimentos, 'dia', 'dias')} com atendimento`}
                comparison={compare('daily_average')}
              />
              <KpiCard label='Pacientes novos' info='prontuarios_new_pacientes' value={formatNumber(current.new_pacientes)} sub='1º atendimento na clínica' comparison={compare('new_pacientes')} />
              <KpiCard
                label='Atendimentos por paciente'
                info='prontuarios_per_paciente'
                value={formatDecimal(current.atendimentos_per_paciente)}
                sub='frequência no período'
                comparison={compare('atendimentos_per_paciente', { neutral: true })}
              />
              <KpiCard
                label='Faltas'
                info='prontuarios_no_show'
                value={formatNumber(current.total_no_show)}
                sub={(current.total_done + current.total_no_show)
                  ? `${formatPercent(current.no_show_rate)} de ${formatNumber(current.total_done + current.total_no_show)} (realizados + faltas)`
                  : 'sem agendamentos realizados ou faltas'}
                comparison={compare('total_no_show', { lowerIsBetter: true })}
              />
            </>
          }
        </div>
      }

      <div className='dashboard-grid'>
        <section className='dashboard-card dashboard-card-wide' aria-label='Por doutor'>
          <div className='dashboard-card-head'>
            <div className='dashboard-card-heading'>
              <CardTitle title='Por doutor' info='prontuarios_by_tecnico' />
              <p className='dashboard-card-sub'>quem atendeu (na sessão sem evolução, o doutor da agenda), não o responsável pelo prontuário · participação sobre o total da clínica</p>
            </div>
          </div>
          {summary.error ?
            <BlockError message={summary.error} onRetry={summary.reload} />
            : !data ?
              <KpiSkeleton count={1} />
              : !byTecnico.length ?
                <p className='dashboard-empty'>Nenhum atendimento no período.</p>
                :
                <div className={(summary.loading && data) ? 'dashboard-stale' : ''}>
                  <div className='dashboard-table-wrap'>
                    <Table size='sm' className='dashboard-table mb-0'>
                      <thead>
                        <tr>
                          <th>Doutor</th>
                          <th className='text-end'>Atendimentos</th>
                          <th className='text-end'>Pacientes</th>
                          <th className='text-end'>Dias</th>
                          {/* No período em andamento, a média de cada doutor vai até ontem (como a do card); os totais, até agora */}
                          <th className='text-end'>{data.compared ? 'Média/dia (até ontem)' : 'Média/dia'}</th>
                          <th className='text-end'>Faltas</th>
                          <th>Participação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byTecnico.map((row) => (
                          <tr key={row.tecnico_id}>
                            <td>{tecnicoLabel(row)}</td>
                            <td className='text-end dashboard-num'>{formatNumber(row.total_atendimentos)}</td>
                            <td className='text-end dashboard-num'>{formatNumber(row.total_pacientes)}</td>
                            <td className='text-end dashboard-num'>{formatNumber(row.days_with_atendimentos)}</td>
                            <td className='text-end dashboard-num'>{formatDecimal(row.daily_average)}</td>
                            <td className='text-end dashboard-num'>{formatNumber(row.total_no_show)}</td>
                            <td className='dashboard-num'>
                              <span className='dashboard-meter' aria-hidden='true'>
                                <i style={{ width: `${Math.min(100, Math.max(0, row.share_percent || 0))}%`, background: chartColors.primary }} />
                              </span>
                              {' '}{formatShare(row.share_percent)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {(!tecnico_id && current && (sumPacientes > current.total_pacientes)) ?
                    <p className='dashboard-card-note mt-2 mb-0'>
                      A soma dos pacientes ({formatNumber(sumPacientes)}) passa do total ({formatNumber(current.total_pacientes)}): um paciente atendido por dois doutores aparece nos dois.
                    </p>
                    : ''}
                </div>
          }
        </section>

        <ChartCard
          wide
          title={dailyTitle}
          info={monthlyByMonth ? 'prontuarios_by_period_month' : 'prontuarios_by_day'}
          subtitle={monthlyByMonth
            ? `${scopeLabel} · acima de 62 dias, um ponto por mês; os meses cortados no início e no fim do período são parciais`
            : `${scopeLabel} · linha = média de ${formatDecimal(dailyAverage)} por dia com atendimento${data?.compared ? ' (até ontem)' : ''} · fim de semana com fundo cinza`}
          {...blockState(days)}
          height={260}
          empty={!dayRows.some((row) => row.total)}
          renderTable={() => (
            // Por mês, o backend não manda pacientes: sem a coluna. Mês que ainda não chegou não é "parcial"
            <DataTable
              head={monthlyByMonth ? ['Mês', 'Atendimentos'] : ['Dia', 'Atendimentos', 'Pacientes']}
              rows={dayRows.map((row) => [
                `${row.title}${row.today ? ' (hoje, até agora)' : ''}${(monthlyByMonth && row.partial && !row.future) ? ' (parcial)' : ''}`,
                row.future ? '—' : formatNumber(row.total),
                ...(monthlyByMonth ? [] : [row.future ? '—' : formatNumber(row.pacientes)]),
              ])}
            />
          )}
        >
          <DailyChart rows={dayRows} average={dailyAverage} narrow={narrow} title={dailyTitle} />
        </ChartCard>

        <ChartCard
          wide
          title={monthsTitle}
          info='prontuarios_by_month'
          subtitle={accumulated ?
            <>
              <span>{scopeLabel} · {accumulated.text}</span>
              <Comparison comparison={accumulated.comparison} />
            </>
            : scopeLabel}
          legend={<MonthlyLegend rows={monthRows} year={monthsYear} previousYear={monthsPreviousYear} />}
          {...blockState(months)}
          height={260}
          empty={!monthRows.some((row) => row.base || row.previousShown)}
          renderTable={() => (
            <DataTable
              head={['Mês', String(monthsPreviousYear), String(monthsYear), 'Variação']}
              rows={monthRows.map((row) => [
                `${row.label}${row.partial ? ' (parcial)' : ''}`,
                row.partial
                  ? partialPreviousText(row)
                  : `${formatNumber(row.previousShown)}${row.previousFaded ? ' (importação)' : ''}`,
                (row.base === null) ? '—' : `${row.partial ? partialBaseText(row) : formatNumber(row.base)}${row.future ? ' (data futura)' : ''}${row.baseFaded ? ' (importação)' : ''}`,
                monthVariationText(row, months.data?.comparison?.status),
              ])}
            />
          )}
        >
          <MonthlyComparisonChart
            rows={monthRows}
            year={monthsYear}
            previousYear={monthsPreviousYear}
            status={months.data?.comparison?.status}
            selected={selectedMonths(period, monthsYear, today)}
            narrow={narrow}
            title={monthsTitle}
          />
        </ChartCard>

        <ReasonsChart block={summary} scopeLabel={scopeLabel} />

        <ChartCard
          title='Média por dia da semana'
          info='prontuarios_weekdays'
          subtitle={`atendimentos ÷ dias daquele dia da semana com atendimento${data?.compared ? ' (até ontem)' : ''}`}
          {...blockState(days)}
          height={220}
          empty={!weekdays.some((row) => row.total_atendimentos)}
          renderTable={() => (
            <DataTable
              head={['Dia da semana', 'Média', 'Atendimentos', 'Dias']}
              rows={weekdays.map((row) => [row.title, formatDecimal(row.average), formatNumber(row.total_atendimentos), formatNumber(row.days_with_atendimentos)])}
            />
          )}
        >
          <WeekdayChart rows={weekdays} title={`Média por dia da semana — ${periodText}`} />
        </ChartCard>
      </div>
    </div>
  )
}

export default TabProntuarios
